import classNames from "classnames";
import React from "react";
import autoBind from "react-autobind";
import PhoneInput from "react-phone-input-2";

import Select from "~/components/forms/dropdowns/SelectWrapper/Select";
import LeadHelpers from "~/helpers/lead_helpers";
import { apiV2Fetch } from "~/lib/fetchClient";

let USER_LICENSES = [];
let USER_INTERESTS = [];
const VISIBLE_ERROR_FIELDS = ["first_name", "last_name", "email", "phone"];
const DEFAULT_FORM_ERROR =
  "We couldn't submit the form. Please try again or contact support.";

class LeadRecruitingApp extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sending: false,
      status: "",
      errors: null,
      user: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        production_label: "",
        transactional_sms_opt_in: false,
        promotional_sms_opt_in: false,
      },
      interest: "",
    };

    const { team } = props;

    USER_LICENSES = [
      { value: "new", label: "Yes, I have a license" },
      { value: "not_licensed", label: "No, I do not have a license" },
      { value: "in_school", label: "I am studying for my license" },
    ];

    USER_INTERESTS = [
      {
        value: "learning",
        label: `Learning more about ${team.name}`,
      },
      {
        value: "starting",
        label: "Starting a career in Real Estate",
      },
      { value: "joining", label: `Joining ${team.name}` },
      {
        value: "meeting",
        label: "Setting up a confidential meeting",
      },
    ];

    autoBind(this);
  }

  clearFieldError(field) {
    this.setState((prevState) => {
      if (!prevState.errors?.[field]) {
        return null;
      }
      const { [field]: _, ...remainingErrors } = prevState.errors;
      return { errors: remainingErrors };
    });
  }

  handleFirstNameChange(e) {
    e.preventDefault();

    const first_name = e.target.value;

    this.clearFieldError("first_name");
    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        first_name,
      },
    }));
  }

  handleLastNameChange(e) {
    e.preventDefault();

    const last_name = e.target.value;

    this.clearFieldError("last_name");
    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        last_name,
      },
    }));
  }

  handleEmailChange(e) {
    e.preventDefault();

    const email = e.target.value;

    this.clearFieldError("email");
    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        email,
      },
    }));
  }

  handlePhoneChange(phone) {
    this.clearFieldError("phone");
    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        phone,
      },
    }));
  }

  handleLicenseChange(selectedItem) {
    const { value } = selectedItem;

    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        production_label: value,
      },
    }));
  }

  handleInterestChange(selectedItem) {
    this.setState({
      interest: selectedItem.value,
    });
  }

  handleSmsOptInChange(e) {
    const { name, checked } = e.target;

    this.setState((prevState) => ({
      user: {
        ...prevState.user,
        [name]: checked,
      },
    }));
  }

  handleFormSubmit(e) {
    e.preventDefault();

    const { user } = this.state;
    const { team, agentId, thank_you_url } = this.props;

    this.setState({
      sending: true,
    });

    user.note = this.buildLeadNote();
    user.source = "Landing Page";
    if (agentId) {
      user.referrer_id = agentId;
    }

    apiV2Fetch
      .post("/leads", {
        json: { ...user },
        headers: { Authorization: `Token token=${team.apiKey}` },
      })
      .then(() => {
        window.location = thank_you_url;
      })
      .catch((error) => {
        const errors =
          error instanceof Error
            ? {
                serverError: [
                  "Our site admins have been notified and we'll have this fixed shortly.",
                ],
              }
            : error;
        this.setState({
          status: "fail",
          sending: false,
          errors,
        });
      });
  }

  getDisplayName() {
    const { team } = this.props;

    if (team.friendlyName && !team.isInOrganization) {
      return team.friendlyName;
    }
    return team.name;
  }

  buildLeadNote() {
    const { interest } = this.state;

    let note = "";

    USER_INTERESTS.forEach((item) => {
      if (item.value === interest) {
        note += `- Interest: ${item.label} \n`;
      }
    });

    return note;
  }

  getFieldError(field) {
    const { errors } = this.state;
    if (!errors || !errors[field]) {
      return null;
    }
    return Array.isArray(errors[field])
      ? errors[field].join(", ")
      : errors[field];
  }

  getFormLevelError() {
    const { errors } = this.state;
    const error = errors?.serverError || errors?.error;

    if (error) {
      return Array.isArray(error) ? error.join(", ") : error;
    }

    return DEFAULT_FORM_ERROR;
  }

  hasFormLevelError() {
    const { errors } = this.state;

    if (!errors) {
      return false;
    }

    if (errors.serverError || errors.error) {
      return true;
    }

    return Object.keys(errors).some(
      (field) => !VISIBLE_ERROR_FIELDS.includes(field),
    );
  }

  render() {
    const { user, interest, sending, status, errors } = this.state;
    const { team, thank_you, url, color } = this.props;

    const style = { backgroundColor: color, borderColor: color };
    const submitButtonClass = classNames("btn btn-primary btn-block btn-lg", {
      disabled: sending,
    });
    let formContent = (
      <form onSubmit={this.handleFormSubmit}>
        {status === "fail" && this.hasFormLevelError() && (
          <div className="alert alert-danger text-left">
            {this.getFormLevelError()}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="user_first_name" className="label">
            First name
          </label>
          <input
            type="text"
            className={classNames("form-control", {
              "is-invalid": this.getFieldError("first_name"),
            })}
            id="user_first_name"
            ref={(input) => (this.userFirstNameInput = input)}
            name="user[first_name]"
            value={user.first_name}
            required
            onChange={this.handleFirstNameChange}
          />
          {this.getFieldError("first_name") && (
            <div className="invalid-feedback">
              {this.getFieldError("first_name")}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="user_last_name" className="label">
            Last name
          </label>
          <input
            type="text"
            className={classNames("form-control", {
              "is-invalid": this.getFieldError("last_name"),
            })}
            id="user_last_name"
            ref={(input) => (this.userLastNameInput = input)}
            name="user[last_name]"
            value={user.last_name}
            required
            onChange={this.handleLastNameChange}
          />
          {this.getFieldError("last_name") && (
            <div className="invalid-feedback">
              {this.getFieldError("last_name")}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="user_email" className="label">
            Email
          </label>
          <input
            type="email"
            className={classNames("form-control", {
              "is-invalid": this.getFieldError("email"),
            })}
            id="user_email"
            ref={(input) => (this.userEmailInput = input)}
            name="user[email]"
            value={user.email}
            required
            onChange={this.handleEmailChange}
          />
          {this.getFieldError("email") && (
            <div className="invalid-feedback">
              {this.getFieldError("email")}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="user_phone" className="label">
            Phone
          </label>
          <PhoneInput
            country="us"
            onlyCountries={["us"]}
            disableCountryCode
            disableDropdown
            placeholder=""
            defaultMask="(...) ...-...."
            value={LeadHelpers.nationalFormatPhoneNumber(user.phone)}
            onChange={this.handlePhoneChange}
            specialLabel={false}
            inputProps={{
              id: "user_phone",
              name: "user[phone]",
              className: classNames("form-control", {
                "is-invalid": this.getFieldError("phone"),
              }),
              type: "phone",
            }}
          />
          {this.getFieldError("phone") && (
            <div className="invalid-feedback d-block">
              {this.getFieldError("phone")}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="user_license" className="label">
            Do you have your Real Estate License?
          </label>
          <Select
            id="user_license"
            options={USER_LICENSES}
            value={user.production_label}
            onChange={this.handleLicenseChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="user_interest" className="label">
            What are you interested in?
          </label>
          <Select
            id="user_interest"
            options={USER_INTERESTS}
            value={interest}
            onChange={this.handleInterestChange}
          />
        </div>

        <div className="form-group text-center text-xs">
          <div className="form-check mb-2">
            <input
              id="transactional_sms_opt_in"
              name="transactional_sms_opt_in"
              className="form-check-input"
              type="checkbox"
              checked={user.transactional_sms_opt_in}
              onChange={this.handleSmsOptInChange}
            />
            <label
              className="form-check-label"
              htmlFor="transactional_sms_opt_in"
            >
              By checking, you are allowing to receive{" "}
              <strong>transactional/informational SMS</strong> communications
              regarding account notifications, appointment reminders, and
              customer care from <strong>{this.getDisplayName()}</strong>.{" "}
              Message frequency may vary. Data rates may apply.{" "}
              <strong>Reply HELP for help or STOP to opt-out.</strong>
            </label>
          </div>

          <div className="form-check mb-2">
            <input
              id="promotional_sms_opt_in"
              name="promotional_sms_opt_in"
              className="form-check-input"
              type="checkbox"
              checked={user.promotional_sms_opt_in}
              onChange={this.handleSmsOptInChange}
            />
            <label
              className="form-check-label"
              htmlFor="promotional_sms_opt_in"
            >
              By checking, you are allowing to receive{" "}
              <strong>promotional/marketing SMS</strong> communications from{" "}
              <strong>{this.getDisplayName()}</strong>. Frequency may vary. Data
              rates may apply.{" "}
              <strong>Reply HELP for help or STOP to opt-out.</strong>
            </label>
          </div>

          <div>
            <a
              href="/home/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>{" "}
            /{" "}
            <a
              href="/home/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
          </div>
        </div>

        <div className="form-group">
          {sending ? (
            <button
              type="submit"
              className={submitButtonClass}
              disabled={sending}
            >
              Sending...
            </button>
          ) : (
            <button type="submit" className={submitButtonClass} style={style}>
              Learn More
            </button>
          )}
        </div>
      </form>
    );

    if (thank_you === true) {
      formContent = (
        <div className="alert alert-success">
          Thanks for requesting to meet the team at {team.name}! We&apos;ve sent
          your information to the Team Leader and you should hear from them
          shortly. Make it a great day! Return to the{" "}
          <a href={url}>previous page.</a>
        </div>
      );
    }

    return (
      <div className="card">
        <div className="card-block">
          <h4 className="card-title">Learn more about us!</h4>
          {formContent}
        </div>
      </div>
    );
  }
}

export default LeadRecruitingApp;
